import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@components-library/Forms/Checkbox/Checkbox';
import { isActionsColumn } from '@components-library/Grid/actions/isActionsColumn';
import { SortIcon } from '@components-library/Icons/iconsComponents';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { Cell, createColumnHelper, flexRender, Header, Row, SortDirection, Table } from '@tanstack/react-table';
import cn from 'classnames';
import styles from './grid.module.scss';

export { createColumnHelper };

interface IGridSelectableCell {
  selectChecked: boolean;
  selectDisabled: boolean;
  selectIndeterminate: boolean;
  isFirstCell: boolean;
  rowSelectionEnabled?: boolean;
}

interface IGridHeaderCell extends IGridSelectableCell {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  header: Header<any, unknown>;
  rowSelectionEnabled?: boolean;
  columnResizeEnabled?: boolean;
  classNameHeaderCell?: string;
  size: number;
  tableHeight: number;
  isSorted: false | SortDirection;
}

export const GridHeaderCell = React.memo(
  ({
    table,
    header,
    rowSelectionEnabled,
    isFirstCell,
    classNameHeaderCell,
    size,
    selectChecked,
    selectDisabled,
    selectIndeterminate,
    columnResizeEnabled,
    tableHeight,
    isSorted,
  }: IGridHeaderCell) => {
    const { i18n } = useTranslation();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
    const headerTextTransformer = <T extends unknown>(header: T) => {
      if (typeof header === 'string') {
        return i18n.t(header as KeyMapKeys);
      } else {
        return header;
      }
    };

    return (
      <th
        className={cn(styles.headerCell, classNameHeaderCell, {
          [styles.firstCell]: isFirstCell,
          [styles.sortable]: header.column.getCanSort(),
        })}
        style={{ width: size }}
        onClick={header.column.getToggleSortingHandler()}>
        <WithSelectRowCheckbox
          isFirstCell={isFirstCell}
          rowSelectionEnabled={rowSelectionEnabled}
          selectDisabled={selectDisabled}
          selectIndeterminate={selectIndeterminate}
          selectChecked={selectChecked}
          table={table}>
          <div className={styles.headerCellText}>
            {header.isPlaceholder
              ? null
              : flexRender(headerTextTransformer(header.column.columnDef.header), header.getContext())}

            {header.column.getCanSort() && <SortIcon selected={isSorted} />}
          </div>
        </WithSelectRowCheckbox>

        {columnResizeEnabled && (
          <div
            {...{
              onClick: (e) => e.stopPropagation(),
              onDoubleClick: () => header.column.resetSize(),
              onMouseDown: header.getResizeHandler(),
              onTouchStart: header.getResizeHandler(),
              className: cn(styles.resizer, styles.ltr, { [styles.isResizing]: header.column.getIsResizing }),
              style: { height: tableHeight },
            }}>
            <div className={styles.draggableLine} style={{ height: tableHeight }} />
          </div>
        )}
      </th>
    );
  },
);

interface IGridCell extends IGridSelectableCell {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: Cell<any, unknown>;
  classNameCell?: string;
  isFirstCell: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row?: Row<any>;
  isResizeEnabled?: boolean;
}

export const GridCell = React.memo(
  ({
    cell,
    classNameCell,
    isFirstCell,
    row,
    rowSelectionEnabled,
    selectChecked,
    selectDisabled,
    selectIndeterminate,
  }: IGridCell) => {
    return (
      <td
        key={cell.id}
        className={cn(styles.cell, classNameCell, cell.column.columnDef.meta?.cellClassName, {
          [styles.actions]: isActionsColumn(cell.column.id),
        })}>
        <WithSelectRowCheckbox
          row={row}
          isFirstCell={isFirstCell}
          rowSelectionEnabled={rowSelectionEnabled}
          selectChecked={selectChecked}
          selectDisabled={selectDisabled}
          selectIndeterminate={selectIndeterminate}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </WithSelectRowCheckbox>
      </td>
    );
  },
);

interface IWithSelectRowCheckbox extends IGridSelectableCell {
  children: React.ReactNode;
  row?: Row<unknown>;
  table?: Table<unknown>;
}

export const WithSelectRowCheckbox = React.memo(
  ({
    children,
    isFirstCell,
    rowSelectionEnabled,
    row,
    table,
    selectChecked,
    selectDisabled,
    selectIndeterminate,
  }: IWithSelectRowCheckbox) => {
    if (!isFirstCell || !rowSelectionEnabled) {
      return <>{children}</>;
    }

    return (
      <div className={styles.selectContainer}>
        <Checkbox
          className={styles.checkbox}
          checked={selectChecked}
          disabled={selectDisabled}
          indeterminate={selectIndeterminate}
          onChange={row?.getToggleSelectedHandler() ?? table?.getToggleAllRowsSelectedHandler()}
          stopPropagation={true}
        />
        {children}
      </div>
    );
  },
);
